import React, { useEffect } from "react";
import { Mail } from "lucide-react";

/**
 * Google AdSense Integration Components
 * Publisher ID: ca-pub-7839624909411981
 * 
 * Ad Slot IDs:
 * - FW Leader (Responsive): 1437416385
 * - FW Square Responsive: 7258311590
 * - FW Rectangle (300x250): 6368523875
 * - FW Large Rectangle (336x280): 9900574773
 * - FW Mobile Res: 8222316588
 */

const ADSENSE_PUBLISHER_ID = "ca-pub-7839624909411981";

// Ad Slot IDs
const AD_SLOTS = {
  leader: "1437416385",
  squareResponsive: "7258311590",
  rectangle: "6368523875",
  largeRectangle: "9900574773",
  mobile: "8222316588"
};

// Real AdSense Ad Component
const AdSenseAd = ({ slotId, format = "auto", style = {}, className = "" }) => {
  const [adError, setAdError] = React.useState(false);
  
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      setAdError(true);
      console.log("AdSense not available");
    }
  }, []);

  // If ad fails, show minimal placeholder
  if (adError) {
    return (
      <div className={`bg-[#F9FAFB] border border-dashed border-[#E0D4C8] h-[50px] flex items-center justify-center ${className}`}>
        <span className="text-[9px] text-[#8B8580] font-mono">AD</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <ins
        className="adsbygoogle"
        style={{ display: "block", minHeight: "50px", ...style }}
        data-ad-client={ADSENSE_PUBLISHER_ID}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

// Leaderboard ad for header/footer areas - Uses FW Leader
export const LeaderboardAd = ({ className = "" }) => {
  return (
    <div className={`w-full flex justify-center py-3 bg-[#F8FAFC] border-y border-[#E0D4C8] ${className}`} data-testid="leaderboard-ad">
      <AdSenseAd slotId={AD_SLOTS.leader} format="auto" />
    </div>
  );
};

// Header sticky banner - Uses FW Leader (responsive)
export const HeaderStickyBanner = ({ onClose }) => {
  const [visible, setVisible] = React.useState(true);
  
  if (!visible) return null;
  
  return (
    <div className="w-full bg-[#F8FAFC] border-b border-[#E0D4C8] py-2 relative" data-testid="header-sticky-banner">
      <div className="max-w-[1400px] mx-auto px-4 flex justify-center">
        <AdSenseAd slotId={AD_SLOTS.leader} format="auto" />
      </div>
      <button 
        onClick={() => setVisible(false)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8B8580] hover:text-[#6B6661] p-1"
        aria-label="Close ad"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// Sidebar ads - Uses FW Rectangle (300x250) and FW Square Responsive
export const SidebarAds = () => {
  return (
    <div className="space-y-6" data-testid="sidebar-ads">
      <div className="bg-white border border-[#E0D4C8] p-4">
        <p className="text-[10px] font-mono text-[#8B8580] uppercase tracking-wider mb-3">Advertisement</p>
        <AdSenseAd slotId={AD_SLOTS.rectangle} style={{ width: "300px", height: "250px" }} />
      </div>
    </div>
  );
};

// In-content ad for articles - Uses FW Large Rectangle (336x280)
export const InContentAd = ({ className = "" }) => {
  return (
    <div className={`my-8 py-6 border-y border-[#E0D4C8] ${className}`} data-testid="in-content-ad">
      <div className="flex justify-center">
        <AdSenseAd slotId={AD_SLOTS.largeRectangle} style={{ width: "336px", height: "280px" }} />
      </div>
      <p className="text-center text-[9px] text-[#8B8580] mt-2 font-mono">
        <a href="mailto:grow@farmwatch.com" className="hover:text-[#0F766E]">Advertise with us</a>
      </p>
    </div>
  );
};

// Mobile sticky footer ad - Uses FW Mobile Res
export const MobileStickyAd = () => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white border-t border-[#E0D4C8] py-2 px-4 flex justify-center" data-testid="mobile-sticky-ad">
      <AdSenseAd slotId={AD_SLOTS.mobile} format="auto" />
    </div>
  );
};

// Article sidebar sticky ad - Uses FW Rectangle
export const ArticleSidebarAd = () => {
  return (
    <div className="sticky top-24 space-y-4" data-testid="article-sidebar-ad">
      <div className="bg-white border border-[#E0D4C8] p-4">
        <p className="text-[10px] font-mono text-[#8B8580] uppercase tracking-wider mb-3">Advertisement</p>
        <AdSenseAd slotId={AD_SLOTS.rectangle} style={{ width: "300px", height: "250px" }} />
        <a href="mailto:grow@farmwatch.com" className="block text-center text-xs text-[#0F766E] mt-3 hover:underline">
          Advertise here
        </a>
      </div>
      
      <div className="bg-[#F0FDF4] border border-[#065F46]/20 p-4">
        <p className="text-[10px] font-mono text-[#065F46] uppercase tracking-wider mb-2">Related Services</p>
        <p className="text-sm text-[#064E3B] mb-3">Connect with agricultural advisors, vets & contractors</p>
        <a href="mailto:grow@farmwatch.com" className="text-xs font-medium text-[#0F766E] hover:underline">
          List your service
        </a>
      </div>
    </div>
  );
};

// Pre-footer large banner - Uses FW Leader
export const PreFooterBanner = () => {
  return (
    <div className="w-full bg-[#1F2937] py-6" data-testid="pre-footer-banner">
      <div className="max-w-[1400px] mx-auto px-4 flex flex-col items-center">
        <p className="text-[10px] font-mono text-[#6B6661] uppercase tracking-wider mb-3">Advertisement</p>
        <AdSenseAd slotId={AD_SLOTS.leader} format="auto" />
      </div>
    </div>
  );
};

// Quadrant divider ad (between news sections) - minimal placeholder
export const QuadrantDividerAd = () => {
  return (
    <div className="w-full py-2" data-testid="quadrant-divider-ad">
      <div className="bg-[#F6E9DD] border border-dashed border-[#D1D5DB] h-[50px] w-full flex items-center justify-center">
        <span className="text-[10px] text-[#8B8580] font-mono uppercase">Advertisement</span>
      </div>
    </div>
  );
};

// Native ad card for marketplace listings
export const NativeListingAd = ({ className = "" }) => {
  return (
    <div 
      className={`bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] border border-[#FCD34D] p-4 ${className}`}
      data-testid="native-listing-ad"
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-[9px] font-mono uppercase tracking-wider text-[#92400E] bg-[#FCD34D] px-2 py-0.5 rounded">
          Sponsored
        </span>
      </div>
      <AdSenseAd slotId={AD_SLOTS.squareResponsive} format="auto" />
    </div>
  );
};

// In-feed promotional ad (between articles)
export const InFeedAd = ({ variant = "default" }) => {
  const variants = {
    default: {
      bg: "bg-gradient-to-r from-[#0F766E] to-[#115E59]",
      title: "Bringing the Farming Community Together",
      subtitle: "Advertise on Farmwatch - Ireland & UK's growing agricultural platform",
      cta: "Get Media Kit"
    },
    equipment: {
      bg: "bg-gradient-to-r from-[#166534] to-[#14532D]",
      title: "Farm Equipment Showcase",
      subtitle: "Feature your machinery and equipment to active buyers",
      cta: "Book Space"
    },
    services: {
      bg: "bg-gradient-to-r from-[#EA580C] to-[#C2410C]",
      title: "Agri Services Directory",
      subtitle: "List your agricultural services - vets, contractors, advisors",
      cta: "List Now"
    },
    adsense: {
      isAdsense: true
    }
  };

  const v = variants[variant] || variants.default;

  if (v.isAdsense) {
    return (
      <div className="w-full py-4" data-testid="in-feed-adsense">
        <div className="max-w-[1200px] mx-auto px-4">
          <AdSenseAd slotId={AD_SLOTS.squareResponsive} format="auto" className="mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${v.bg} text-white p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`} data-testid="in-feed-promo">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-white/60 mb-1">Advertisement</p>
        <h3 className="font-serif text-lg sm:text-xl mb-1">{v.title}</h3>
        <p className="text-sm text-white/80">{v.subtitle}</p>
      </div>
      <a 
        href="mailto:grow@farmwatch.com"
        className="px-5 py-2 bg-white text-[#0F766E] font-medium text-sm hover:bg-white/90 transition-colors whitespace-nowrap"
      >
        {v.cta}
      </a>
    </div>
  );
};

// Forum/Community thread ad - Uses FW Rectangle
export const ForumThreadAd = () => {
  return (
    <div className="bg-[#FFFBEB] border border-[#FCD34D] p-4 my-4" data-testid="forum-thread-ad">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[9px] font-mono uppercase tracking-wider text-[#92400E] bg-[#FEF3C7] px-2 py-0.5">
              Sponsored Post
            </span>
          </div>
          <AdSenseAd slotId={AD_SLOTS.rectangle} style={{ width: "300px", height: "250px" }} className="mx-auto" />
        </div>
      </div>
    </div>
  );
};

// Stock news in-feed ad - Uses FW Square Responsive
export const StockNewsInFeedAd = () => {
  return (
    <div className="bg-gradient-to-r from-[#0F766E]/10 to-[#064E3B]/10 border border-[#0F766E]/20 p-4" data-testid="stock-news-infeed-ad">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[9px] font-mono uppercase tracking-wider text-[#065F46] bg-[#D1FAE5] px-2 py-0.5">
          Featured Investment
        </span>
      </div>
      <AdSenseAd slotId={AD_SLOTS.squareResponsive} format="auto" />
      <p className="text-[9px] text-[#6B6661] mt-2 text-center">
        <a href="mailto:grow@farmwatch.com" className="hover:text-[#0F766E]">Promote your agri-investment opportunity</a>
      </p>
    </div>
  );
};

// Sponsor banner with branding
export const SponsorBanner = ({ position = "top" }) => {
  return (
    <div className="w-full bg-[#F8FAFC] border-y border-[#E0D4C8] py-3">
      <div className="max-w-[1200px] mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
        <div className="flex items-center gap-4 sm:gap-6">
          <span className="text-[10px] font-mono text-[#8B8580] uppercase tracking-wider">Sponsored by</span>
          
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="w-20 sm:w-24 h-8 bg-[#E5E7EB] rounded flex items-center justify-center">
              <span className="text-[9px] sm:text-[10px] text-[#8B8580]">Sponsor 1</span>
            </div>
            <div className="w-20 sm:w-24 h-8 bg-[#E5E7EB] rounded flex items-center justify-center">
              <span className="text-[9px] sm:text-[10px] text-[#8B8580]">Sponsor 2</span>
            </div>
            <div className="hidden sm:flex w-24 h-8 bg-[#E5E7EB] rounded items-center justify-center">
              <span className="text-[10px] text-[#8B8580]">Sponsor 3</span>
            </div>
          </div>
        </div>
        
        <a 
          href="mailto:grow@farmwatch.com" 
          className="text-[10px] font-mono text-[#0F766E] hover:underline flex items-center gap-1"
        >
          <Mail size={10} />
          Become a sponsor
        </a>
      </div>
    </div>
  );
};

// Legacy placeholder for backwards compatibility
export const AdPlaceholder = ({ size = "leaderboard", className = "" }) => {
  const slotMap = {
    leaderboard: AD_SLOTS.leader,
    rectangle: AD_SLOTS.rectangle,
    largeRectangle: AD_SLOTS.largeRectangle,
    mobileBanner: AD_SLOTS.mobile,
    inFeed: AD_SLOTS.squareResponsive,
    banner: AD_SLOTS.leader,
    skyscraper: AD_SLOTS.rectangle,
    largeBanner: AD_SLOTS.leader,
  };

  return (
    <div className={className}>
      <AdSenseAd slotId={slotMap[size] || AD_SLOTS.squareResponsive} format="auto" />
    </div>
  );
};

export default AdPlaceholder;
